// src/hooks/plateService.js
import API_ENDPOINTS from "../apiConfig";
import { authFetch } from "../utils/authFetch";

/**
 * Fetch license plates for a single area
 * @param {string|number} areaId
 * @returns {Promise<Array>} array of plates
 */
export async function fetchPlatesForArea(areaId) {
  if (!areaId) return [];

  const token = localStorage.getItem("token");
  if (!token) throw new Error("No token found. Please log in.");

  const response = await authFetch(`${API_ENDPOINTS.getareas}/${areaId}/license-plates`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) throw new Error("Failed to fetch plates");

  const data = await response.json();
  return (data || []).map((p, idx) => ({
    id: p.id ?? `temp-${Date.now()}-${idx}`,
    plate: p.plate ?? "",
    valid_from: p.valid_from ?? "",
    valid_until: p.valid_until ?? "",
    note: p.note ?? "",
    categories: p.categories ?? [],
    created_at: p.created_at ?? null,
    updated_at: p.updated_at ?? null,
  }));
}

/**
 * Fetch per-gate sync status for all plates in an area.
 * Returns { [plateId]: [{ gate_id, gate_name, gate_direction, display, synced_at, last_attempt_at }] }
 * `display` is one of 'ok' | 'pending' (still within grace period, don't show a warning) | 'warning'.
 * @param {string|number} areaId
 * @returns {Promise<Object>}
 */
export async function fetchPlateSyncStatus(areaId) {
  if (!areaId) return {};

  const response = await authFetch(`${API_ENDPOINTS.getareas}/${areaId}/license-plates/sync-status`);

  if (!response.ok) throw new Error("Failed to fetch plate sync status");

  return (await response.json()) || {};
}

/**
 * Plates deleted in the app that the camera still accepts (removal not confirmed yet,
 * still valid by date): [{ id, plate, deleted_at, valid_until, gates: [{ gate_name, gate_direction }] }].
 */
export async function fetchDeletedStillActive(areaId) {
  if (!areaId) return [];

  const response = await authFetch(`${API_ENDPOINTS.getareas}/${areaId}/license-plates/deleted-still-active`);

  if (!response.ok) throw new Error("Failed to fetch deleted plates still active");

  return (await response.json()) || [];
}

/**
 * Fetch plates for multiple areas in parallel
 * @param {Array} areas - array of {id: string|number}
 * @returns {Promise<Object>} object mapping areaId -> plates array
 */
export async function fetchPlatesForAreas(areas) {
  if (!areas || areas.length === 0) return {};

  const results = {};
  await Promise.all(
    areas.map(async (area) => {
      // gate openers may not read plates (licenseplate_read) — don't ask, the API would answer 403
      if (area.user_role === "gate_opener") {
        results[area.id] = [];
        return;
      }
      try {
        const plates = await fetchPlatesForArea(area.id);
        results[area.id] = plates;
      } catch (err) {
        console.error("Error fetching plates for area", area.id, err);
        results[area.id] = [];
      }
    })
  );
  return results;
}
